import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useLocation, Link } from 'react-router-dom';

import { Container, Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import type {
    StudentMasteryByStandardDTO,
    StudentMasteryByDomainDTO,
} from './models/standardsReportDTOs';
import StudentStandardMasteryTable from './components/StudentStandardMasteryTable';
import StudentDomainMasteryTable from './components/StudentDomainMasteryTable';

type LocationState = {
    standards?: Array<{ standardId: number; standardCode: string }>;
};

const StandardsStudentDetailsPage: React.FC = () => {
    document.title = 'Standards – Student Details';

    const { state, search } = useLocation();
    const sp = new URLSearchParams(search);

    const studentId = Number(sp.get('studentId') || '0');
    const isSeed = sp.get('seed') === '1';

    const standardIdsParam = sp.get('standardIds') || '';
    const standardIdsFromUrl = standardIdsParam
        .split(',')
        .map((id) => Number(id.trim()))
        .filter((id) => !Number.isNaN(id) && id > 0);

    const standardsFromState = ((state as LocationState | null)?.standards ?? []) as Array<{
        standardId: number;
        standardCode: string;
    }>;

    const standards =
        standardsFromState.length > 0
            ? standardsFromState
            : standardIdsFromUrl.map((id) => ({ standardId: id, standardCode: '' }));

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [standardRows, setStandardRows] = useState<StudentMasteryByStandardDTO[]>([]);
    const [domainRows, setDomainRows] = useState<StudentMasteryByDomainDTO[]>([]);

    useEffect(() => {
        async function fetchData() {
            if (!studentId || Number.isNaN(studentId)) {
                setError('Invalid student id.');
                setLoading(false);
                return;
            }
            if (standards.length === 0) {
                setError('No data to display. Return to the Standards Report to try again.');
                setLoading(false);
                return;
            }

            try {
                setError('');
                setLoading(true);

                const perStandard = await Promise.all(
                    standards.map(async (s) => {
                        const path = isSeed
                            ? `reports/standardsReport/mastery/student/standard/seed?studentId=${studentId}&standardId=${s.standardId}`
                            : `reports/standardsReport/mastery/student/standard?studentId=${studentId}&standardId=${s.standardId}`;
                        const { data } = await apiRequest<StudentMasteryByStandardDTO>('GET', path);
                        return data;
                    })
                );

                const domainIds = [
                    ...new Set(perStandard.map((m) => m.standardDomainMasterId).filter(Boolean)),
                ];
                const perDomain = await Promise.all(
                    domainIds.map(async (domId) => {
                        const path = isSeed
                            ? `reports/standardsReport/mastery/student/domain/seed?studentId=${studentId}&standardDomainMasterId=${domId}`
                            : `reports/standardsReport/mastery/student/domain?studentId=${studentId}&standardDomainMasterId=${domId}`;
                        const { data } = await apiRequest<StudentMasteryByDomainDTO>('GET', path);
                        return data;
                    })
                );

                perStandard.sort((a, b) => a.standardCode.localeCompare(b.standardCode));
                perDomain.sort((a, b) =>
                    (a.domainShortName ?? '').localeCompare(b.domainShortName ?? '')
                );

                setStandardRows(perStandard);
                setDomainRows(perDomain);
            } catch {
                setError('Failed to load Standards Student Details.');
            } finally {
                setLoading(false);
            }
        }

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container>
            <div className="mb-3">
                <Link to="/reports/standards" className="underline hover:no-underline">
                    ← Back to Standards Report
                </Link>
            </div>

            <h1 className="text-center text-black py-2">Standards Student Details</h1>

            {loading && <Loading message="Loading Standards Student Details..." />}
            {error && (
                <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <ErrorMessage error={error} />
                </Box>
            )}

            {!loading && !error && (
                <>
                    <StudentStandardMasteryTable data={standardRows} />

                    <StudentDomainMasteryTable data={domainRows} />
                </>
            )}
        </Container>
    );
};

export default StandardsStudentDetailsPage;
